/**
 * Design System §7: timestamps arrive as `"00.18:30"` — a dot where a colon belongs. Parse
 * defensively and never render the raw string.
 */

/** Accepts `HH:MM:SS`, `HH.MM:SS`, `MM:SS`, or a bare number of seconds. Returns null if unparsable. */
export function parseTimestampSeconds(raw: string | number | null | undefined): number | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;

  const trimmed = raw.trim();
  if (trimmed === '') return null;

  const parts = trimmed.split(/[.:]/).map(part => Number(part));
  if (parts.some(part => !Number.isFinite(part))) return null;

  // Read right-to-left so a missing hour segment is handled without a special case.
  const [seconds = 0, minutes = 0, hours = 0] = [...parts].reverse();
  return hours * 3600 + minutes * 60 + seconds;
}

/** `18:30` under an hour, `1:04:12` over. Always colon-separated, whatever arrived. */
export function formatClock(raw: string | number | null | undefined): string {
  const total = parseTimestampSeconds(raw);
  if (total === null) return '—';

  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = Math.floor(total % 60);
  const pad = (value: number) => String(value).padStart(2, '0');

  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}

const DATE_TIME = new Intl.DateTimeFormat('es-MX', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'America/Mexico_City',
});

const DATE_ONLY = new Intl.DateTimeFormat('es-MX', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'America/Mexico_City',
});

const DATE_TIME_LONG = new Intl.DateTimeFormat('es-MX', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
  timeZone: 'America/Mexico_City',
});

/** Broadcast times are read in the newsroom's timezone, never the browser's. */
export function formatDateTime(iso: string | null | undefined): string {
  const date = toDate(iso);
  return date ? DATE_TIME.format(date) : '—';
}

/** `lunes, 21 de septiembre de 2026, 07:27 a.m.` — the stream header's kicker line. */
export function formatDateTimeLong(iso: string | null | undefined): string {
  const date = toDate(iso);
  if (!date) return '—';
  return DATE_TIME_LONG.format(date).replace('a. m.', 'a.m.').replace('p. m.', 'p.m.');
}

export function formatDate(iso: string | null | undefined): string {
  const date = toDate(iso);
  return date ? DATE_ONLY.format(date) : '—';
}

/** Groups broadcasts by newsroom-local day — `2026-08-04`, not the UTC day. */
export function mexicoCityDayKey(iso: string | null | undefined): string {
  const date = toDate(iso);
  if (!date) return 'sin-fecha';
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'America/Mexico_City',
  }).format(date);
}

const HOUR_MINUTE = new Intl.DateTimeFormat('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/Mexico_City' });
const DAY_MONTH = new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'long', timeZone: 'America/Mexico_City' });

/** `18:30`, no seconds — for an already-occurred instant rather than a broadcast offset. */
export function formatHourMinute(iso: string | null | undefined): string {
  const date = toDate(iso);
  return date ? HOUR_MINUTE.format(date) : '—';
}

const DAY_LONG = new Intl.DateTimeFormat('es-MX', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'America/Mexico_City',
});
const CLOCK_MERIDIEM = new Intl.DateTimeFormat('es-MX', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
  timeZone: 'America/Mexico_City',
});

/** `Martes, 23 de septiembre de 2026` — the newsroom day in long form, weekday capitalized,
 *  for page headers that answer "what day is it where the newsroom is". `now` stays a
 *  parameter so the header's live tick does not hide the formatter behind the clock. */
export function formatDayLong(now: Date): string {
  const text = DAY_LONG.format(now);
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** `09:41 a.m.` — the newsroom wall clock with meridiem, companion to `formatDayLong`. */
export function formatMeridiem(now: Date): string {
  return CLOCK_MERIDIEM.format(now).replace('a. m.', 'a.m.').replace('p. m.', 'p.m.');
}

/** `ahora` · `hace 2 min` · `hace 1 h` · `hace 3 días` — how long since an instant, for
 *  "última captura" style figures. */
export function formatAgo(iso: string | null | undefined, now: Date): string {
  const date = toDate(iso);
  if (!date) return '—';
  const minutes = Math.floor((now.getTime() - date.getTime()) / 60_000);
  if (minutes < 1) return 'ahora';
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return `hace ${days} ${days === 1 ? 'día' : 'días'}`;
}

/** "Hoy", "Ayer", or the plain date further back — the judgment both `dayLabel` and
 *  `relativeDayTime` build on. `now` is a parameter so it stays testable without a live clock. */
function relativeDay(iso: string | null | undefined, now: Date): string {
  const date = toDate(iso);
  if (!date) return 'Sin fecha';

  const key = mexicoCityDayKey(iso);
  if (key === mexicoCityDayKey(now.toISOString())) return 'Hoy';

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (key === mexicoCityDayKey(yesterday.toISOString())) return 'Ayer';

  return DAY_MONTH.format(date);
}

/** "Hoy · 18 de septiembre", "Ayer · 17 de septiembre", or plain "16 de septiembre" further back —
 *  the header a history list groups its rows under. */
export function dayLabel(iso: string | null | undefined, now: Date): string {
  const date = toDate(iso);
  if (!date) return 'Sin fecha';

  const relative = relativeDay(iso, now);
  return relative === 'Hoy' || relative === 'Ayer' ? `${relative} · ${DAY_MONTH.format(date)}` : relative;
}

/** "HOY · 10:42" — a compact single-line timestamp for a feed row, as opposed to `dayLabel`'s
 *  full-date group header. */
export function relativeDayTime(iso: string | null | undefined, now: Date): string {
  return `${relativeDay(iso, now).toUpperCase()} · ${formatHourMinute(iso)}`;
}

function toDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}
